import React from "react";
import { motion } from "framer-motion";

const STEPS = [
  {
    title: "Listen",
    description:
      "I start by understanding your organisation, culture, challenges and ambitions, to uncover where friction exists and what success looks like.",
  },
  {
    title: "Frame",
    description:
      "I bring external perspective, regulatory insight and industry experience to help leadership see risks and opportunities differently.",
  },
  {
    title: "Transform",
    description:
      "Together we develop practical, defensible solutions that improve decision-making and withstand regulatory scrutiny.",
  },
  {
    title: "Embed",
    description:
      "I stay close through landing — coaching teams, briefing committees, and adjusting until the change holds.",
  },
];

export const Approach = () => {
  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="relative py-16 md:py-24 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-24">
          <div className="lg:col-span-7">
            <p className="font-sans-body text-xs tracking-widest-2 uppercase text-[#3E7373] mb-6 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#3E7373]" />
              How I work
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#0D0D0D] font-medium">
              An approach
              <br />
              <span className="italic text-[#3E7373]">grounded in the work.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 font-sans-body text-[#4A4A4A] text-base md:text-lg leading-relaxed self-end">
            My engagements are intentionally focused and senior-led throughout. You work directly with me from start to finish, benefiting from deep expertise, continuity, and a highly tailored approach.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-14 relative">
          {/* Decorative connecting line on lg */}
          <div className="hidden lg:block absolute top-7 left-[12.5%] right-[12.5%] h-px bg-[#9CDBD9]/40" aria-hidden="true" />

          {STEPS.map((step, idx) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: idx * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`approach-step-${idx}`}
              className="relative"
            >
              <div className="flex items-center gap-4 mb-6">
                <span className="relative z-10 w-14 h-14 rounded-full bg-[#F9F8F6] border border-[#0D0D0D]/20 flex items-center justify-center font-serif-display text-xl text-[#0D0D0D]">
                  {String(idx + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="font-serif-display text-2xl md:text-[28px] text-[#0D0D0D] mb-3">
                {step.title}
              </h3>
              <p className="font-sans-body text-[15px] leading-relaxed text-[#4A4A4A] max-w-xs">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Approach;
